import { ExclamationCircleFilled } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import type { HookAPI } from "antd/es/modal/useModal";
import {
  assignmentScheduleActions,
  type ScheduleChangeSource,
} from "../../features/assignment-schedule/assignmentScheduleSlice";
import type {
  TechnicianAssignmentSchedule,
  TechnicianAssignmentScheduleStatusEnum,
} from "../generated-axios-client";

export const STATUS_TAG_COLOR: Record<TechnicianAssignmentScheduleStatusEnum, string> = {
  SCHEDULED: "processing",
  ACTIVE: "success",
  ENDING: "warning",
  COMPLETED: "default",
  CANCELLED: "default",
  SKIPPED: "default",
};

const EDITABLE_STATUSES: TechnicianAssignmentScheduleStatusEnum[] = [
  "SCHEDULED",
  "ACTIVE",
  "ENDING",
];
const CANCELLABLE_STATUSES: TechnicianAssignmentScheduleStatusEnum[] = [
  "SCHEDULED",
  "ACTIVE",
];

export const isBookingEditable = (booking: TechnicianAssignmentSchedule) =>
  EDITABLE_STATUSES.includes(booking.status);

export const isBookingCancellable = (booking: TechnicianAssignmentSchedule) =>
  CANCELLABLE_STATUSES.includes(booking.status);

// Cancelling a running booking ends it today, which the server records as COMPLETED;
// the cancelling user tells the two apart.
export const shownBookingStatus = (
  booking: TechnicianAssignmentSchedule
): TechnicianAssignmentScheduleStatusEnum =>
  booking.status === "COMPLETED" && booking.cancelled_by != null
    ? "CANCELLED"
    : booking.status;

export const bookingStatusLabel = (
  intl: IntlShape,
  booking: TechnicianAssignmentSchedule
) =>
  intl.formatMessage({
    id: `schedule_status_${shownBookingStatus(booking).toLowerCase()}`,
  });

export const confirmCancelBooking = ({
  booking,
  technicianName,
  intl,
  modalApi,
  dispatch,
  source,
}: {
  booking: TechnicianAssignmentSchedule;
  technicianName: string;
  intl: IntlShape;
  modalApi: HookAPI | undefined;
  dispatch: Dispatch;
  source: ScheduleChangeSource;
}) =>
  modalApi?.confirm({
    title: intl.formatMessage(
      {
        id:
          booking.status === "ACTIVE"
            ? "schedule_cancel_active_confirmation"
            : "schedule_cancel_confirmation",
      },
      { name: technicianName }
    ),
    icon: <ExclamationCircleFilled />,
    okText: intl.formatMessage({ id: "confirm" }),
    okType: "danger",
    cancelText: intl.formatMessage({ id: "cancel" }),
    onOk() {
      if (!booking.id) return;
      dispatch(
        assignmentScheduleActions.cancelSchedule({ scheduleId: booking.id, source })
      );
    },
  });
